/**
 * QueueKanbanColumn - Coluna fixa do Kanban da Fila de Atendimento
 * 
 * Versão: 3.0.0 - Cada coluna busca seus próprios dados via useServiceQueueColumn
 * 
 * Implementa infinite scroll com IntersectionObserver
 * e paginação independente por coluna (arquitetura correta)
 */

import { useRef, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { QueueKanbanCard } from './QueueKanbanCard';
import { useServiceQueueColumn } from '@/hooks/useServiceQueueColumn';
import type { QueueItem, QueueSituation, QueueFilters } from '@/hooks/useServiceQueue';

// Configuração das colunas fixas
export interface QueueColumnConfig {
  id: QueueSituation;
  name: string;
  description: string;
  color: string;
}

// Colunas pré-definidas da fila de atendimento
export const QUEUE_COLUMNS: QueueColumnConfig[] = [
  { id: 'waiting_bot', name: 'No Bot', description: 'Conversas no fluxo automatizado', color: '#8b5cf6' },
  { id: 'waiting_agent', name: 'Aguardando Agente', description: 'Aguardando atribuição de agente', color: '#f97316' },
  { id: 'pending_transfer', name: 'Transferências', description: 'Transferências pendentes', color: '#06b6d4' },
  { id: 'in_service', name: 'Em Atendimento', description: 'Sendo atendidas por agentes', color: '#22c55e' },
  { id: 'closed', name: 'Encerrado', description: 'Conversas finalizadas', color: '#6b7280' },
];

/**
 * Converte cor hex para rgba com opacidade
 */
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

interface QueueKanbanColumnProps {
  column: QueueColumnConfig;
  /** Filtros globais da fila (departamento, inbox, agente, busca) */
  filters?: Partial<QueueFilters>;
  /** Contagem total da RPC de stats (mais precisa que items.length) */
  totalCount?: number;
  /** Callback ao clicar em um item */
  onItemClick?: (item: QueueItem) => void;
}

export function QueueKanbanColumn({
  column,
  filters,
  totalCount,
  onItemClick,
}: QueueKanbanColumnProps) {
  // =========================================================================
  // Hook de dados da coluna - cada coluna busca seus próprios dados
  // =========================================================================
  const {
    items,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useServiceQueueColumn(column.id, filters);

  // Ref para o elemento trigger de infinite scroll
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // =========================================================================
  // Infinite scroll via IntersectionObserver
  // =========================================================================
  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage || !fetchNextPage) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Quando o elemento fica visível e não está carregando, busca mais
        if (entry.isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { 
        threshold: 0.1, 
        rootMargin: '100px' // Começa a carregar 100px antes de chegar ao fim
      }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  // Usa totalCount da RPC de stats se disponível, senão usa items.length
  const displayCount = totalCount !== undefined ? totalCount : items.length;

  return (
    <div
      className={cn(
        'flex flex-col w-72 h-full shrink-0 rounded-lg bg-muted/30',
        'border border-border/50 overflow-hidden'
      )}
    >
      {/* Header da Coluna - Com fundo colorido sutil */}
      <div 
        className="px-3 py-2 border-b border-border/50 rounded-t-lg"
        style={{ backgroundColor: hexToRgba(column.color, 0.15) }}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {/* Indicador circular de cor */}
            <span 
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: column.color }}
            />
            <span className="font-semibold text-xs truncate">
              {column.name}
            </span>
            <span className="text-[10px] text-muted-foreground bg-background/50 px-1.5 py-0.5 rounded">
              {displayCount}
            </span>
          </div>
        </div>
        {/* Descrição da coluna */}
        <p className="text-[11px] text-muted-foreground mt-1 truncate">
          {column.description}
        </p>
      </div>

      {/* Lista de Cards */}
      <ScrollArea className="flex-1 h-0 min-h-0 p-2">
        <div className="space-y-2 min-h-[100px]">
          {/* Estado de loading inicial */}
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              Nenhuma conversa
            </div>
          ) : (
            <>
              {items.map((item) => (
                <QueueKanbanCard
                  key={item.id}
                  item={item}
                  onClick={() => onItemClick?.(item)}
                />
              ))}
              
              {/* Trigger de infinite scroll */}
              {hasNextPage && (
                <div ref={loadMoreRef} className="h-4" />
              )}
              
              {/* Loading indicator para próxima página */}
              {isFetchingNextPage && (
                <div className="flex justify-center py-2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
